import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box,
    Button,
    Checkbox,
    Container,
    Loading,
    MenuItem,
    Select,
    Stack,
    styled,
    Text,
    TextInput,
} from 'shell/components';

const ACTIVITY_TYPES = [
    { value: 'ai_grading', label: 'AI Grading Activity' },
    { value: 'ai_feedback', label: 'AI Feedback Assignment' },
    { value: 'rubric_review', label: 'Rubric Feedback Tool' },
    { value: 'submission_analysis', label: 'Student Submission Analysis' },
    { value: 'document_search', label: 'Course Document Search' },
    { value: 'course_assistant', label: 'Course AI Assistant' },
];

const getFormAction = () => {
    const baseUrl = axios.defaults.baseURL || process.env.REACT_APP_API_ENDPOINT || '';
    return `${baseUrl.replace(/\/$/, '')}/lti/deep-linking/response`;
};

const DeepLinkPickerPage = () => {
    const location = useLocation();
    const sessionId = useMemo(() => new URLSearchParams(location.search).get('session'), [location.search]);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [title, setTitle] = useState('');
    const [activityType, setActivityType] = useState(ACTIVITY_TYPES[0].value);
    const [enableStudentLaunch, setEnableStudentLaunch] = useState(true);
    const [requireInstructorReview, setRequireInstructorReview] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const loadSession = async () => {
            if (!sessionId) {
                setError(true);
                setLoading(false);
                return;
            }

            try {
                const result = await axios.get('/lti/deep-linking/session', {
                    params: { session: sessionId },
                });
                if (cancelled) return;

                setSession(result);
                setTitle('AI Grading Activity');
            } catch (e) {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadSession();

        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    if (loading) return <Loading />;

    if (error || !session) {
        return (
            <Container>
                <CenteredStack alignItems={'center'} justifyContent={'center'} spacing={2}>
                    <Text component={'h1'} variant={'h5'} weight={'semibold'}>
                        Deep Linking session unavailable
                    </Text>
                    <Text color={'light'}>Please reopen TimelyGrader from your LMS.</Text>
                </CenteredStack>
            </Container>
        );
    }

    return (
        <Container>
            <CenteredStack alignItems={'center'} justifyContent={'center'}>
                <PickerSurface as={'form'} action={getFormAction()} method={'POST'}>
                    <input type={'hidden'} name={'session_id'} value={session.id} />
                    <input type={'hidden'} name={'enable_student_launch'} value={`${enableStudentLaunch}`} />
                    <input type={'hidden'} name={'require_instructor_review'} value={`${requireInstructorReview}`} />
                    <Stack spacing={4}>
                        <Stack spacing={1}>
                            <Text component={'h1'} variant={'h5'} weight={'semibold'}>
                                Create LMS Activity
                            </Text>
                            <Text color={'light'}>{session.course_id}</Text>
                        </Stack>
                        <Stack spacing={3}>
                            <Select
                                label={'Activity type'}
                                labelId={'lti-activity-type-label'}
                                name={'activity_type'}
                                value={activityType}
                                onChange={(event) => setActivityType(event.target.value)}
                            >
                                {ACTIVITY_TYPES.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <TextInput
                                label={'Title'}
                                name={'title'}
                                required
                                value={title}
                                onChange={(event) => setTitle(event.target.value)}
                            />
                            <Stack spacing={1}>
                                <Checkbox
                                    checked={enableStudentLaunch}
                                    label={'Enable student launch'}
                                    onChange={(event) => setEnableStudentLaunch(event.target.checked)}
                                />
                                <Checkbox
                                    checked={requireInstructorReview}
                                    label={'Require instructor review'}
                                    onChange={(event) => setRequireInstructorReview(event.target.checked)}
                                />
                            </Stack>
                        </Stack>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'}>
                            <Button type={'submit'} disabled={!title.trim()}>
                                Add to Course
                            </Button>
                        </Stack>
                    </Stack>
                </PickerSurface>
            </CenteredStack>
        </Container>
    );
};

const CenteredStack = styled(Stack)(({ theme }) => ({
    minHeight: '100%',
    paddingBlock: theme.spacing(6),
}));

const PickerSurface = styled(Box)(({ theme }) => ({
    width: 'min(100%, 560px)',
    padding: theme.spacing(4),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: theme.spacing(1),
    background: theme.palette.ensightful.shades[0],
}));

export default DeepLinkPickerPage;
